import React, { useState, useEffect, useCallback } from 'react';
import {
  Users,
  UserPlus,
  Mail,
  CheckCircle,
  XCircle,
  Clock,
  Shield,
  Trash2,
  Edit3,
  X,
  Eye,
  BarChart2,
  MessageSquare,
  Briefcase,
  User,
  ChevronRight,
  RefreshCw,
  AlertCircle,

  LayoutGrid,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  listTeam,
  inviteMember,
  updatePermissions,
  revokeAccess,
  acceptInvite,
  declineInvite,
  defaultPermissions,
} from '../../services/userAdmin';
import type {
  TeamMember,
  SharedAccess,
  TeamPermissions,
} from '../../services/userAdmin';

// ---------------------------------------------------------------------------
// Permission config – label + icon for each toggleable permission
// ---------------------------------------------------------------------------
const PERMISSION_CONFIG: {
  key: keyof TeamPermissions;
  label: string;
  icon: React.FC<{ className?: string }>;
  description: string;
}[] = [
  {
    key: 'profile',
    label: 'Profile',
    icon: ({ className }) => <User className={className} />,
    description: 'View profile info, links & collaborations',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: ({ className }) => <BarChart2 className={className} />,
    description: 'View profile view & click stats',
  },
  {
    key: 'leads',
    label: 'Leads',
    icon: ({ className }) => <MessageSquare className={className} />,
    description: 'View contact form submissions',
  },
  {
    key: 'collaborations',
    label: 'Collaborations',
    icon: ({ className }) => <Briefcase className={className} />,
    description: 'View collaboration partners',
  },
];

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Confirm Modal (replaces browser window.confirm)
// ---------------------------------------------------------------------------
interface ConfirmModalProps {
  title: string;
  description: string;
  confirmLabel?: string;
  danger?: boolean;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  title,
  description,
  confirmLabel = 'Confirm',
  danger = false,
  loading = false,
  onConfirm,
  onCancel,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
      <div className="flex items-start gap-3 mb-4">
        <div className={`p-2 rounded-full flex-shrink-0 ${danger ? 'bg-red-100' : 'bg-yellow-100'}`}>
          <AlertCircle className={`h-5 w-5 ${danger ? 'text-red-600' : 'text-yellow-600'}`} />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          disabled={loading}
          className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${
            danger
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-primary-600 hover:bg-primary-700'
          }`}
        >
          {loading ? 'Please wait…' : confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Permission presets
// ---------------------------------------------------------------------------
const PERMISSION_PRESETS: {
  label: string;
  emoji: string;
  description: string;
  perms: TeamPermissions;
}[] = [
  {
    label: 'Viewer',
    emoji: '👁️',
    description: 'Profile only',
    perms: { profile: true, analytics: false, leads: false, collaborations: false },
  },
  {
    label: 'Analyst',
    emoji: '📊',
    description: 'Analytics + Leads',
    perms: { profile: false, analytics: true, leads: true, collaborations: false },
  },
  {
    label: 'Manager',
    emoji: '🤝',
    description: 'Full access',
    perms: { profile: true, analytics: true, leads: true, collaborations: true },
  },
];

const statusBadge = (status: TeamMember['status']) => {
  switch (status) {
    case 'active':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="h-3 w-3" />
          Active
        </span>
      );
    case 'pending':
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
          <Clock className="h-3 w-3" />
          Pending
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          <XCircle className="h-3 w-3" />
          Revoked
        </span>
      );
  }
};

const permissionChips = (perms: TeamPermissions) =>
  PERMISSION_CONFIG.filter((p) => perms[p.key]).map((p) => (
    <span
      key={p.key}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200"
    >
      <p.icon className="h-3 w-3" />
      {p.label}
    </span>
  ));

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// ---------------------------------------------------------------------------
// Invite Modal
// ---------------------------------------------------------------------------
interface InviteModalProps {
  onClose: () => void;
  onInvited: () => void;
}

const InviteModal: React.FC<InviteModalProps> = ({ onClose, onInvited }) => {
  const [email, setEmail] = useState('');
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [perms, setPerms] = useState<TeamPermissions>(defaultPermissions());
  const [saving, setSaving] = useState(false);

  const toggle = (key: keyof TeamPermissions) => {
    setActivePreset(null);
    setPerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const applyPreset = (preset: typeof PERMISSION_PRESETS[number]) => {
    setActivePreset(preset.label);
    setPerms({ ...preset.perms });
  };

  const allGranted = Object.values(perms).every(Boolean);
  const toggleAll = () => {
    setActivePreset(null);
    setPerms(
      allGranted
        ? { profile: false, analytics: false, leads: false, collaborations: false }
        : { profile: true, analytics: true, leads: true, collaborations: true }
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    const anyPerm = Object.values(perms).some(Boolean);
    if (!anyPerm) {
      toast.error('Grant at least one permission');
      return;
    }
    setSaving(true);
    try {
      const res = await inviteMember({ email: email.trim(), permissions: perms });
      if (res.data.success) {
        toast.success(res.data.message);
        onInvited();
        onClose();
      } else {
        toast.error(res.data.message || 'Failed to send invite');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to send invite');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Invite Team Member</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="team@example.com"
                required
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The person must have a MyOneQR account to accept the invite.
            </p>
          </div>

          {/* Permissions */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Permissions
              </label>
              <button
                type="button"
                onClick={toggleAll}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                <LayoutGrid className="h-3 w-3" />
                {allGranted ? 'Remove All' : 'Select All'}
              </button>
            </div>

            {/* Presets */}
            <div className="flex gap-2 mb-3">
              {PERMISSION_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => applyPreset(preset)}
                  title={preset.description}
                  className={`flex-1 flex flex-col items-center py-2 px-1 rounded-lg border text-xs font-medium transition-colors ${
                    activePreset === preset.label
                      ? 'border-primary-400 bg-primary-50 text-primary-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-base mb-0.5">{preset.emoji}</span>
                  {preset.label}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              {PERMISSION_CONFIG.map((p) => (
                <label
                  key={p.key}
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    perms[p.key]
                      ? 'border-primary-400 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={perms[p.key]}
                    onChange={() => toggle(p.key)}
                    className="mt-0.5 h-4 w-4 rounded accent-primary-600"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p.icon className="h-4 w-4 text-primary-600" />
                      <span className="text-sm font-medium text-gray-800">{p.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">{p.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
            >
              {saving ? 'Sending…' : 'Send Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Edit Permissions Modal
// ---------------------------------------------------------------------------
interface EditPermissionsModalProps {
  member: TeamMember;
  onClose: () => void;
  onSaved: () => void;
}

const EditPermissionsModal: React.FC<EditPermissionsModalProps> = ({
  member,
  onClose,
  onSaved,
}) => {
  const [perms, setPerms] = useState<TeamPermissions>({ ...member.permissions });
  const [saving, setSaving] = useState(false);

  const toggle = (key: keyof TeamPermissions) =>
    setPerms((prev) => ({ ...prev, [key]: !prev[key] }));

  const allGranted = Object.values(perms).every(Boolean);
  const toggleAll = () =>
    setPerms(
      allGranted
        ? { profile: false, analytics: false, leads: false, collaborations: false }
        : { profile: true, analytics: true, leads: true, collaborations: true }
    );

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updatePermissions(member.id, perms);
      if (res.data.success) {
        toast.success('Permissions updated');
        onSaved();
        onClose();
      } else {
        toast.error(res.data.message || 'Failed to update');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to update permissions');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary-600" />
              <h2 className="text-lg font-semibold text-gray-900">Edit Permissions</h2>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{member.invite_email}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-4 w-4 text-gray-500" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-500">Toggle individual permissions below</span>
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              <LayoutGrid className="h-3 w-3" />
              {allGranted ? 'Remove All' : 'Select All'}
            </button>
          </div>
          {PERMISSION_CONFIG.map((p) => (
            <label
              key={p.key}
              className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                perms[p.key]
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="checkbox"
                checked={perms[p.key]}
                onChange={() => toggle(p.key)}
                className="mt-0.5 h-4 w-4 rounded accent-primary-600"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p.icon className="h-4 w-4 text-primary-600" />
                  <span className="text-sm font-medium text-gray-800">{p.label}</span>
                </div>
                <span className="text-xs text-gray-500">{p.description}</span>
              </div>
            </label>
          ))}
        </div>

        <div className="flex gap-3 px-6 pb-5">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-60 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// My Team tab
// ---------------------------------------------------------------------------
type StatusFilter = 'all' | 'active' | 'pending' | 'revoked';

interface MyTeamTabProps {
  members: TeamMember[];
  onRefresh: () => void;
}

const MyTeamTab: React.FC<MyTeamTabProps> = ({ members, onRefresh }) => {
  const navigate = useNavigate();
  const [showInvite, setShowInvite]       = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<TeamMember | null>(null);
  const [revokingId, setRevokingId]       = useState<number | null>(null);
  const [statusFilter, setStatusFilter]   = useState<StatusFilter>('all');

  const filteredMembers =
    statusFilter === 'all' ? members : members.filter(m => m.status === statusFilter);

  const handleRevoke = async () => {
    if (!confirmRevoke) return;
    setRevokingId(confirmRevoke.id);
    try {
      const res = await revokeAccess(confirmRevoke.id);
      if (res.data.success) {
        toast.success('Access revoked');
        onRefresh();
      } else {
        toast.error(res.data.message || 'Failed to revoke');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to revoke access');
    } finally {
      setRevokingId(null);
      setConfirmRevoke(null);
    }
  };

  return (
    <>
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        {/* Status filter chips */}
        <div className="flex items-center gap-1 flex-wrap">
          {(['all', 'active', 'pending', 'revoked'] as StatusFilter[]).map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize transition-colors ${
                statusFilter === f
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {f === 'all'
                ? `All (${members.length})`
                : `${f.replace('_', ' ')} (${members.filter(m => m.status === f).length})`}
            </button>
          ))}
        </div>

        {/* Invite button */}
        <button
          onClick={() => setShowInvite(true)}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors flex-shrink-0"
        >
          <UserPlus className="h-4 w-4" />
          Invite Member
        </button>
      </div>

      {/* Empty state */}
      {filteredMembers.length === 0 && members.length === 0 && (
        <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No team members yet</p>
          <p className="text-sm text-gray-400 mt-1 mb-4">
            Invite someone to give them access to your dashboard
          </p>
          <button
            onClick={() => setShowInvite(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <UserPlus className="h-4 w-4" />
            Invite Member
          </button>
        </div>
      )}

      {filteredMembers.length === 0 && members.length > 0 && (
        <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-2xl">
          <p className="text-sm text-gray-500">No members with status "{statusFilter}"</p>
          <button
            onClick={() => setStatusFilter('all')}
            className="mt-1 text-xs text-primary-600 hover:underline"
          >
            Show all
          </button>
        </div>
      )}

      {/* Member cards */}
      <div className="space-y-3">
        {filteredMembers.map((member) => (
          <div
            key={member.id}
            className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
          >
            <div className="flex items-start justify-between gap-3">
              {/* Left: avatar + info */}
              <div className="flex items-start gap-3 min-w-0">
                <div className="h-9 w-9 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-semibold text-primary-700">
                    {(member.granted_user_name ?? member.invite_email)
                      .charAt(0)
                      .toUpperCase()}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {member.granted_user_name ?? '—'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">{member.invite_email}</p>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {statusBadge(member.status)}
                    <span className="text-xs text-gray-400">
                      Invited {formatDate(member.invited_at)}
                    </span>
                    {member.accepted_at && (
                      <span className="text-xs text-gray-400">
                        · Accepted {formatDate(member.accepted_at)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {/* View their dashboard (only active grants) */}
                {member.status === 'active' && member.granted_user_id && (
                  <button
                    onClick={() => navigate(`/dashboard/team-view/${member.granted_user_id}`)}
                    title="Preview as this member"
                    className="p-1.5 rounded-lg text-gray-400 hover:text-green-600 hover:bg-green-50 transition-colors"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                )}
                <button
                  onClick={() => setEditingMember(member)}
                  title="Edit permissions"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  <Edit3 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setConfirmRevoke(member)}
                  disabled={revokingId === member.id}
                  title="Revoke access"
                  className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Permissions row */}
            <div className="mt-3 flex items-center gap-1.5 flex-wrap">
              <Shield className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              {permissionChips(member.permissions).length > 0
                ? permissionChips(member.permissions)
                : (
                  <span className="text-xs text-gray-400 italic">No permissions granted</span>
                )}
            </div>
          </div>
        ))}
      </div>

      {/* Modals */}
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvited={onRefresh}
        />
      )}
      {editingMember && (
        <EditPermissionsModal
          member={editingMember}
          onClose={() => setEditingMember(null)}
          onSaved={onRefresh}
        />
      )}
      {confirmRevoke && (
        <ConfirmModal
          title="Remove team member?"
          description={`${confirmRevoke.invite_email} will immediately lose access to your dashboard.`}
          confirmLabel="Remove Access"
          danger
          loading={revokingId === confirmRevoke.id}
          onConfirm={handleRevoke}
          onCancel={() => setConfirmRevoke(null)}
        />
      )}
    </>
  );
};

// ---------------------------------------------------------------------------
// Shared With Me tab
// ---------------------------------------------------------------------------
interface SharedWithMeTabProps {
  accesses: SharedAccess[];
  onRefresh: () => void;
}

const SharedWithMeTab: React.FC<SharedWithMeTabProps> = ({ accesses, onRefresh }) => {
  const navigate = useNavigate();
  const [actioningId, setActioningId] = useState<number | null>(null);

  const handleAccept = async (access: SharedAccess) => {
    setActioningId(access.id);
    try {
      const res = await acceptInvite(access.id);
      if (res.data.success) {
        toast.success('Invite accepted! You now have access.');
        onRefresh();
      } else {
        toast.error(res.data.message || 'Failed to accept');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to accept invite');
    } finally {
      setActioningId(null);
    }
  };

  const handleDecline = async (access: SharedAccess) => {
    if (!window.confirm(`Decline invite from ${access.owner_name}?`)) return;
    setActioningId(access.id);
    try {
      const res = await declineInvite(access.id);
      if (res.data.success) {
        toast.success('Invite declined');
        onRefresh();
      } else {
        toast.error(res.data.message || 'Failed to decline');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Failed to decline invite');
    } finally {
      setActioningId(null);
    }
  };

  // Separate pending from active
  const pending = accesses.filter(a => a.status === 'pending');
  const active  = accesses.filter(a => a.status === 'active');

  return (
    <div className="space-y-6">
      {accesses.length === 0 && (
        <div className="text-center py-14 border-2 border-dashed border-gray-200 rounded-2xl">
          <Users className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No shared access yet</p>
          <p className="text-sm text-gray-400 mt-1">
            When someone invites you to their dashboard, it will appear here.
          </p>
        </div>
      )}

      {/* Pending invites */}
      {pending.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Clock className="h-4 w-4 text-yellow-500" />
            Pending Invites
            <span className="ml-1 px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium">
              {pending.length}
            </span>
          </h3>
          <div className="space-y-3">
            {pending.map((access) => (
              <div
                key={access.id}
                className="bg-yellow-50 border border-yellow-200 rounded-xl p-4"
              >
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-yellow-200 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-semibold text-yellow-800">
                      {access.owner_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {access.owner_name}
                    </p>
                    <p className="text-xs text-gray-500">{access.owner_email}</p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      Invited {formatDate(access.invited_at)}
                    </p>
                    {/* Permissions they're granting */}
                    <div className="flex items-center gap-1.5 flex-wrap mt-2">
                      <AlertCircle className="h-3.5 w-3.5 text-yellow-600 flex-shrink-0" />
                      <span className="text-xs text-yellow-700 font-medium">Granting access to:</span>
                      {permissionChips(access.permissions)}
                    </div>
                  </div>
                </div>
                {/* Accept / Decline */}
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => handleAccept(access)}
                    disabled={actioningId === access.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-60 transition-colors"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Accept
                  </button>
                  <button
                    onClick={() => handleDecline(access)}
                    disabled={actioningId === access.id}
                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-300 text-red-600 text-sm font-medium hover:bg-red-50 disabled:opacity-60 transition-colors"
                  >
                    <XCircle className="h-4 w-4" />
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Active accesses */}
      {active.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            Active Access
          </h3>
          <div className="space-y-3">
            {active.map((access) => (
              <div
                key={access.id}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-full overflow-hidden bg-green-100 flex items-center justify-center flex-shrink-0 border border-gray-100">
                      {access.profile_image ? (
                        <img
                          src={access.profile_image}
                          alt={access.owner_name}
                          className="h-full w-full object-cover"
                          onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                        />
                      ) : (
                        <span className="text-sm font-semibold text-green-700">
                          {access.owner_name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{access.owner_name}</p>
                      {access.tagline && (
                        <p className="text-xs text-primary-600 italic">{access.tagline}</p>
                      )}
                      <p className="text-xs text-gray-500">{access.owner_email}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Accepted {access.accepted_at ? formatDate(access.accepted_at) : '—'}
                      </p>
                    </div>
                  </div>

                  {/* View Dashboard button */}
                  <button
                    onClick={() => navigate(`/dashboard/team-view/${access.owner_id}`)}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary-600 text-white text-xs font-medium hover:bg-primary-700 transition-colors"
                  >
                    <Eye className="h-3.5 w-3.5" />
                    View Dashboard
                    <ChevronRight className="h-3.5 w-3.5" />
                  </button>
                </div>

                {/* Permissions chips */}
                <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                  <Shield className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                  {permissionChips(access.permissions).length > 0
                    ? permissionChips(access.permissions)
                    : <span className="text-xs text-gray-400 italic">No permissions</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main UserAdmin component
// ---------------------------------------------------------------------------
const UserAdmin: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'my_team' | 'shared_with_me'>('my_team');
  // Switch to shared_with_me if arriving with pending invites
  const [myTeam, setMyTeam] = useState<TeamMember[]>([]);
  const [sharedWithMe, setSharedWithMe] = useState<SharedAccess[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const pendingCount = sharedWithMe.filter((a) => a.status === 'pending').length;

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listTeam();
      if (res.data.success) {
        setMyTeam(res.data.my_team ?? []);
        setSharedWithMe(res.data.shared_with_me ?? []);
      } else {
        setError('Failed to load team data');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load team data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="h-6 w-6 text-primary-600" />
              Team Access
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Invite people to access your dashboard or view dashboards shared with you.
            </p>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            title="Refresh"
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-6">
        <button
          onClick={() => setActiveTab('my_team')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'my_team'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Users className="h-4 w-4" />
          My Team
          {myTeam.length > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold">
              {myTeam.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('shared_with_me')}
          className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'shared_with_me'
              ? 'bg-white text-primary-700 shadow-sm'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Shield className="h-4 w-4" />
          Shared With Me
          {pendingCount > 0 && (
            <span className="px-1.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 text-xs font-semibold">
              {pendingCount}
            </span>
          )}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Tab content */}
      {!loading && activeTab === 'my_team' && (
        <MyTeamTab members={myTeam} onRefresh={fetchData} />
      )}
      {!loading && activeTab === 'shared_with_me' && (
        <SharedWithMeTab accesses={sharedWithMe} onRefresh={fetchData} />
      )}
    </div>
  );
};

export default UserAdmin;
